// src/pages/DiaryDetail.js — 일기 상세 페이지 (/diary/:diaryId)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import TimeCapsuleWrite from '../components/TimeCapsuleWrite';
import TimeCapsuleView from '../components/TimeCapsuleView';
import ConfirmModal from '../components/ConfirmModal';
import { getUserInfo, parseLocalDate } from '../utils/storageUtils';
import { apiFetch } from '../utils/apiFetch';
import '../styles/DiaryDetail.css';
import { useToast } from '../components/Toast';
import { isMobileDevice } from '../utils/deviceUtils';

const DiaryDetail = () => {
    const toast = useToast();
    const navigate = useNavigate();
    const { diaryId } = useParams();
    const [diary, setDiary] = useState(null);
    const [isLoadingDiary, setIsLoadingDiary] = useState(true);
    const [isTimeCapsuleWriteOpen, setIsTimeCapsuleWriteOpen] = useState(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [timeCapsules, setTimeCapsules] = useState([]);
    const [isLoadingTimeCapsules, setIsLoadingTimeCapsules] = useState(false);
    const [capsuleError, setCapsuleError] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [isSharing, setIsSharing] = useState(false);

    // URL의 diaryId로 자체 조회 — 부모가 데이터를 주입하던 구조 폐기
    const fetchDiary = useCallback(async () => {
        setIsLoadingDiary(true);
        try {
            const response = await apiFetch(`/diaries/${diaryId}`, {
                method: 'GET'
            });

            if (response.ok) {
                const result = await response.json();
                setDiary(result.data);
            } else {
                const errorData = await response.json().catch(() => null);
                toast.error(errorData?.errorMessage || '일기를 불러오지 못했습니다.');
                navigate('/diary', { replace: true });
            }
        } catch (error) {
            console.error('일기 상세 조회 오류:', error);
            toast.error('네트워크 오류가 발생했습니다.');
            navigate('/diary', { replace: true });
        } finally {
            setIsLoadingDiary(false);
        }
        // toast는 렌더마다 새 객체라 의존성에서 제외 (Home.fetchCoupleInfo와 같은 예외)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [diaryId, navigate]);

    // ✅ 개봉일 전까지만 타임캡슐 작성/수정 가능 (로컬 날짜 기준)
    // 개봉일까지 남은 일수 (로컬 날짜 기준 — isDiaryOpened와 같은 계산이라 판정 불일치 없음)
    const daysUntilOpen = () => {
        if (!diary || !diary.openDate) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return Math.round((parseLocalDate(diary.openDate) - today) / (1000 * 60 * 60 * 24));
    };

    const isDiaryOpened = () => {
        if (!diary || !diary.openDate) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return parseLocalDate(diary.openDate) <= today;
    };

    const fetchTimeCapsules = useCallback(async () => {
        if (!diaryId) return;

        setIsLoadingTimeCapsules(true);
        setCapsuleError(null);

        try {
            const response = await apiFetch(`/timecapsule/${diaryId}`, {
                method: 'GET'
            });

            if (response.ok) {
                const result = await response.json();

                if (result.data && result.data.timeCapsuleList && Array.isArray(result.data.timeCapsuleList)) {
                    setTimeCapsules(result.data.timeCapsuleList);
                } else {
                    setTimeCapsules([]);
                }
            } else {
                const errorData = await response.json().catch(() => null);
                setCapsuleError(errorData?.errorMessage || '타임캡슐을 불러오지 못했습니다.');
                setTimeCapsules([]);
            }
        } catch (error) {
            console.error('타임캡슐 조회 오류:', error);
            setCapsuleError('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
            setTimeCapsules([]);
        } finally {
            setIsLoadingTimeCapsules(false);
        }
    }, [diaryId]);

    useEffect(() => {
        // 라우트 가드 — 토큰 없이 직접 URL 진입 시 로그인으로
        if (!localStorage.getItem('accessToken')) {
            navigate('/login');
            return;
        }

        const user = getUserInfo();
        setCurrentUser(user);

        fetchDiary();
        fetchTimeCapsules();
    }, [fetchDiary, fetchTimeCapsules, navigate]);

    // 기존 함수들...
    const formatDate = (dateString) => {
        const [year, month, day] = dateString.split('-');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'long'
        });
    };

    const handleEditClick = () => {
        navigate(`/diary/${diaryId}/edit`);
    };

    const handleTimeCapsuleWriteClick = () => {
        // ✅ 개봉 여부 확인
        if (isDiaryOpened()) {
            toast.error('이미 개봉된 일기에는 타임캡슐을 작성할 수 없습니다.');
            return;
        }
        setIsTimeCapsuleWriteOpen(true);
    };

    const handleTimeCapsuleWriteClose = () => {
        setIsTimeCapsuleWriteOpen(false);
    };

    const handleTimeCapsuleWriteSuccess = () => {
        setIsTimeCapsuleWriteOpen(false);
        fetchTimeCapsules();
    };

    const handleDeleteClick = () => {
        setIsDeleteConfirmOpen(true);
    };

    const copyShareText = async (shareText) => {
        try {
            await navigator.clipboard.writeText(shareText);
            toast.success('공유 문구를 복사했어요. 원하는 곳에 붙여넣기(Ctrl+V)해주세요.');
        } catch (error) {
            console.error('클립보드 복사 실패:', error);
            toast.error('복사에 실패했습니다. 브라우저 권한을 확인해주세요.');
        }
    };

    // 상대에게 알리기 — 모바일은 OS 공유 시트(카톡 등 앱을 사용자가 직접 선택)를 띄운다.
    // 카카오 API를 쓰지 않아 동의항목·팀원 등록·검수가 전혀 필요 없다.
    // PC는 공유 시트가 있어도 쓰기 번거로워(앱 연동 부족) 바로 클립보드 복사로 간다.
    // 모바일이라도 공유 시트 미지원 브라우저는 클립보드 복사로 폴백.
    const handleShareClick = async () => {
        if (isSharing) return;
        setIsSharing(true);
        const shareText = [
            '💌 그날의 우리 — 새 일기가 도착했어요!',
            `"${diary.title}" (${formatDate(diary.date)})`,
            `${window.location.origin}/diary/${diaryId}`,
        ].join('\n');
        try {
            if (isMobileDevice() && navigator.share) {
                // 링크를 text에 포함 — url 필드만 전달하는 앱에서도 문구·링크가 함께 가게 한다
                await navigator.share({ text: shareText });
            } else {
                await copyShareText(shareText);
            }
        } catch (error) {
            // 공유 시트를 그냥 닫은 경우(AbortError)는 실패가 아니다
            if (error.name !== 'AbortError') {
                console.error('공유 실패:', error);
                await copyShareText(shareText);
            }
        } finally {
            setIsSharing(false);
        }
    };

    const handleDeleteConfirm = async () => {
        setIsDeleteLoading(true);

        try {
            const response = await apiFetch(`/diaries/${diaryId}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                toast.success('일기가 삭제되었습니다.');
                navigate('/diary'); // 돌아간 캘린더가 재조회하므로 별도 갱신 불필요
            } else {
                const errorData = await response.json();
                toast.error(errorData.errorMessage || errorData.message || '일기 삭제에 실패했습니다.');
            }
        } catch (error) {
            console.error('일기 삭제 오류:', error);
            toast.error('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
        } finally {
            setIsDeleteLoading(false);
            setIsDeleteConfirmOpen(false);
        }
    };

    if (isLoadingDiary || !diary) {
        return (
            <div className="diary-detail-page">
                <AppHeader />
                <div className="loading-spinner"></div>
            </div>
        );
    }

    // 내가 작성한 타임캡슐 찾기
    const myTimeCapsule = timeCapsules.find(tc => tc.memberId === currentUser?.id);

    // 상대방이 작성한 타임캡슐 찾기
    const partnerTimeCapsule = timeCapsules.find(tc => tc.memberId !== currentUser?.id);

    // 개봉일 확인 (로컬 날짜 기준)
    const isTimeCapsuleOpened = (openDate) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return parseLocalDate(openDate) <= today;
    };

    // ✅ 타임캡슐 작성 가능 여부 확인 (개봉일 전까지)
    const canCreateTimeCapsule = !isDiaryOpened();

    return (
        <>
            <div className="diary-detail-page">
                <AppHeader />
                <div className="diary-detail-panel">
                    <div className="detail-header">
                        <div className="detail-title-section">
                            <h2>{diary.title}</h2>
                            <p className="diary-date">{formatDate(diary.date)}</p>
                        </div>
                        <div className="header-actions">
                            <button onClick={handleEditClick} className="edit-btn" title="수정">
                                ✏️
                            </button>
                            <button
                                onClick={handleDeleteClick}
                                className="delete-btn"
                                disabled={isDeleteLoading}
                                title="삭제"
                            >
                                {isDeleteLoading ? '⏳' : '🗑️'}
                            </button>
                            <button onClick={() => navigate('/diary')} className="detail-close-btn">✕</button>
                        </div>
                    </div>

                    <div className="detail-content">
                        {/* 일기 내용 */}
                        <div className="diary-content">
                            {(diary.blocks && diary.blocks.length > 0
                                ? diary.blocks
                                : [{ blockId: 'legacy', fontColor: '#000000', content: diary.content }]
                            ).map((block, blockIndex) => (
                                <div
                                    key={block.blockId ?? `block-${blockIndex}`}
                                    className="content-block"
                                    style={{ color: block.fontColor }}
                                >
                                    {block.content.split('\n').map((line, index) => (
                                        <p key={index} className="content-line">
                                            {line || '\u00A0'}
                                        </p>
                                    ))}
                                </div>
                            ))}
                        </div>

                        {/* 타임캡슐 섹션 */}
                        <div className="timecapsule-section">
                            <div className="timecapsule-header">
                                <h3>⏰ 타임캡슐</h3>
                                <p className="timecapsule-subtitle">이 일기에 연결된 비밀 메시지</p>
                            </div>

                            {/* ✅ 개봉 완료 안내 메시지 */}
                            {!canCreateTimeCapsule && (
                                <div className="timecapsule-restriction-notice">
                                    <div className="restriction-icon">⏰</div>
                                    <div className="restriction-content">
                                        <p><strong>타임캡슐이 개봉되었습니다</strong></p>
                                        <p>개봉일 전까지만 타임캡슐을 작성할 수 있습니다.</p>
                                        {diary.openDate && <small>개봉일: {formatDate(diary.openDate)}</small>}
                                    </div>
                                </div>
                            )}

                            {isLoadingTimeCapsules ? (
                                <div className="timecapsule-loading">
                                    <div className="loading-spinner"></div>
                                    <p>타임캡슐을 불러오는 중...</p>
                                </div>
                            ) : capsuleError ? (
                                <div className="timecapsule-error">
                                    <p>{capsuleError}</p>
                                    <button className="capsule-retry-btn" onClick={fetchTimeCapsules}>
                                        다시 시도
                                    </button>
                                </div>
                            ) : (
                                <div className="timecapsule-grid">
                                    {/* 내 타임캡슐 */}
                                    <div className="timecapsule-card my-timecapsule">
                                        <div className="timecapsule-card-header">
                                            <h4>💌 내 타임캡슐</h4>
                                        </div>

                                        {/* 작성 가능 기간에만 개봉일·마감 안내 (개봉 후에는 상단 안내문이 개봉일을 보여준다) */}
                                        {canCreateTimeCapsule && diary.openDate && (
                                            <div className="timecapsule-open-info">
                                                <p>🔒 {formatDate(diary.openDate)} 개봉 · D-{daysUntilOpen()}</p>
                                                <p className={`timecapsule-open-deadline${daysUntilOpen() === 1 ? ' deadline-urgent' : ''}`}>
                                                    {daysUntilOpen() === 1
                                                        ? '⚠️ 오늘까지만 작성할 수 있어요!'
                                                        : '(개봉 전까지 작성할 수 있어요)'}
                                                </p>
                                            </div>
                                        )}

                                        {myTimeCapsule ? (
                                            <TimeCapsuleView
                                                timeCapsule={myTimeCapsule}
                                                isOwner={true}
                                                isOpened={isTimeCapsuleOpened(myTimeCapsule.openDate)}
                                                onUpdate={fetchTimeCapsules}
                                                canEdit={canCreateTimeCapsule} // ✅ 수정 가능 여부 전달
                                            />
                                        ) : (
                                            <div className="timecapsule-empty">
                                                {canCreateTimeCapsule ? (
                                                    <>
                                                        <p>아직 타임캡슐을 작성하지 않았습니다.</p>
                                                        <button
                                                            className="add-timecapsule-btn"
                                                            onClick={handleTimeCapsuleWriteClick}
                                                        >
                                                            타임캡슐 작성하기 🎁
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <p>타임캡슐 작성 기간이 만료되었습니다.</p>
                                                        <div className="empty-timecapsule-icon">⏰</div>
                                                        <small>개봉일 전까지만 작성 가능</small>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* 상대방 타임캡슐 */}
                                    <div className="timecapsule-card partner-timecapsule">
                                        <div className="timecapsule-card-header">
                                            <h4>💝 상대방 타임캡슐</h4>
                                        </div>

                                        {partnerTimeCapsule ? (
                                            <TimeCapsuleView
                                                timeCapsule={partnerTimeCapsule}
                                                isOwner={false}
                                                isOpened={isTimeCapsuleOpened(partnerTimeCapsule.openDate)}
                                                onUpdate={fetchTimeCapsules}
                                                canEdit={false} // 상대방 타임캡슐은 수정 불가
                                            />
                                        ) : (
                                            <div className="timecapsule-empty">
                                                <p>상대방이 아직 타임캡슐을 작성하지 않았습니다.</p>
                                                <div className="empty-timecapsule-icon">📭</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="detail-actions">
                        {/* 모바일: OS 공유 시트 / PC·미지원 브라우저: 클립보드 복사 */}
                        <button
                            className="share-action-btn"
                            onClick={handleShareClick}
                            disabled={isSharing}
                        >
                            {isSharing ? '공유 중...' : '💌 공유'}
                        </button>
                        <button className="edit-action-btn" onClick={handleEditClick}>
                            수정
                        </button>
                        <button
                            className="delete-action-btn"
                            onClick={handleDeleteClick}
                            disabled={isDeleteLoading}
                        >
                            {isDeleteLoading ? '삭제 중...' : '삭제'}
                        </button>
                        <button className="close-btn" onClick={() => navigate('/diary')}>
                            닫기
                        </button>
                    </div>
                </div>
            </div>

            {/* 타임캡슐 작성 모달 */}
            {isTimeCapsuleWriteOpen && (
                <TimeCapsuleWrite
                    diary={diary}
                    onClose={handleTimeCapsuleWriteClose}
                    onSuccess={handleTimeCapsuleWriteSuccess}
                />
            )}

            {/* 일기 삭제 확인 모달 */}
            {isDeleteConfirmOpen && (
                <ConfirmModal
                    title="일기 삭제"
                    message={'정말로 이 일기를 삭제하시겠습니까?\n삭제된 일기는 복구할 수 없습니다.'}
                    confirmText="삭제"
                    isLoading={isDeleteLoading}
                    onConfirm={handleDeleteConfirm}
                    onClose={() => setIsDeleteConfirmOpen(false)}
                />
            )}
        </>
    );
};

export default DiaryDetail;
